/**
 * Helpers for the notification system: time-ago formatting and type → icon mapping.
 */

import type { LucideIcon } from "lucide-react"
import {
  FileText,
  Lightbulb,
  TrendingUp,
  Receipt,
  Package,
  MessageCircleQuestion,
  CalendarCheck,
  CheckCircle2,
  Building2,
  ClipboardCheck,
  Bell,
  Sparkles,
  UserPlus,
  AlertTriangle,
} from "lucide-react"

export type NotificationType =
  | "report"
  | "score"
  | "supply"
  | "maintenance"
  | "schedule"
  | "scheduled"
  | "reminder"
  | "insight"
  | "invoice"
  | "client"
  | "question"
  | "lead_invited"
  | "apply_capture"
  | "demo_booked"
  | "portal_update_draft"
  | "incident"

export interface NotificationTypeStyle {
  icon: LucideIcon
  iconBg: string
  iconColor: string
}

const TYPE_STYLES: Record<string, NotificationTypeStyle> = {
  report: {
    icon: FileText,
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-400",
  },
  insight: {
    icon: Lightbulb,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
  },
  score: {
    icon: TrendingUp,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
  },
  invoice: {
    icon: Receipt,
    iconBg: "bg-white/[0.06]",
    iconColor: "text-white/40",
  },
  supply: {
    icon: Package,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
  },
  question: {
    icon: MessageCircleQuestion,
    iconBg: "bg-indigo-500/10",
    iconColor: "text-indigo-400",
  },
  scheduled: {
    icon: CalendarCheck,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
  },
  schedule: {
    icon: CalendarCheck,
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-400",
  },
  reminder: {
    icon: CheckCircle2,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
  },
  client: {
    icon: Building2,
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-400",
  },
  lead_invited: {
    icon: UserPlus,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
  },
  apply_capture: {
    icon: UserPlus,
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-400",
  },
  demo_booked: {
    icon: CalendarCheck,
    iconBg: "bg-sky-500/10",
    iconColor: "text-sky-400",
  },
  maintenance: {
    icon: ClipboardCheck,
    iconBg: "bg-white/[0.06]",
    iconColor: "text-white/40",
  },
  portal_update_draft: {
    icon: Sparkles,
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-400",
  },
  incident: {
    icon: AlertTriangle,
    iconBg: "bg-rose-500/10",
    iconColor: "text-rose-400",
  },
}

const DEFAULT_STYLE: NotificationTypeStyle = {
  icon: Bell,
  iconBg: "bg-white/[0.06]",
  iconColor: "text-white/40",
}

export function getNotificationTypeStyle(type: string): NotificationTypeStyle {
  return TYPE_STYLES[type] ?? DEFAULT_STYLE
}

/** Format a timestamp as "2m ago", "1h ago", "Yesterday", "3d ago", or full date. */
export function formatTimeAgo(createdAt: string): string {
  const date = new Date(createdAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMins < 1) return "Just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}
